<template>
  <div class="w-full pt-5 flex flex-col gap-2.5 items-center">
    <div
      ref="scrollableAssetsContainer"
      class="h-52 w-full flex flex-col divide-y divide-dashboard-grey"
      :class="{ 'overflow-y-auto': canSeeMoreRows, 'overflow-y-hidden': !canSeeMoreRows }"
    >
      <label
        v-for="({ asset_id, asset_name, commaSeparatedFormatEmissions, formattedEmissions }, index) in assets"
        :key="asset_id"
        :for="`asset-checkbox-${index}-${uid}`"
        class="w-full flex items-center justify-between py-1.5"
      >
        <div class="flex items-center gap-1">
          <CubeIcon />
          <span class="text-[#627285] text-sm no-localize-translate">{{ asset_name }}</span>
        </div>
        <div class="flex items-center gap-3">
          <span
            v-if="selectedAssets.includes(asset_id)"
            class="p-0"
            @click="unCheckAsset(asset_id)"
          >
            <CheckRoundedSmall :custom-style="`w-3 h-3 text-white cursor-pointer`" />
          </span>
          <input
            v-else
            :disabled="selectedAssets.length === 2 && !assets[index].checked"
            type="checkbox"
            class="p-1 rounded-full bg-slate-100 border-1 border-slate-100 text-main-primary focus:ring-transparent"
            @change="checkAsset(asset_id)"
          >
          <span
            class="text-sm text-[#627285] w-[115px] text-right no-localize-translate"
            :title="commaSeparatedFormatEmissions"
          >{{ formattedEmissions }} <span class="no-localize-translate text-[#627285]/80">tCO<sub>2</sub>e</span></span>
        </div>
      </label>
    </div>
    <button
      v-if="assets.length > 6"
      type="button"
      class="outline-none text-sm text-[#0574B6]"
      @click="toggleViewableAssets"
    >
      {{ canSeeMoreRows ? 'View Less' : 'View More' }}
    </button>
  </div>
</template>

<script>
export default {
  name: 'EmissionsBreakdownAssetsSelector',

  props: {
    breakdown: {
      type: Array,
      required: true,
    },
  },

  emits: ['assetsSelected'],

  data() {
    return {
      canSeeMoreRows: false,
      assets: this.transformAssets(this.breakdown),
      selectedAssets: [],
      uid: null,
    }
  },

  watch: {
    breakdown: {
      handler(newAssets) {
        this.assets = this.transformAssets(newAssets)
        const selectedAssets = [...this.assets]
          .filter(({ checked }) => checked)
          .map(({ asset_id, asset_name, total_emissions, categories }) => ({
            asset_id,
            asset_name,
            total_emissions,
            categories,
          }))
        this.selectedAssets = selectedAssets.map(asset => asset.asset_id)
        this.$emit('assetsSelected', selectedAssets)
      },
      deep: true,
    },
  },
  mounted() {
    this.uid = this.$.uid
  },
  methods: {
    checkAsset(assetId) {
      this.selectedAssets.push(assetId)
      const assetIndex = this.assets.findIndex(({ asset_id }) => asset_id === assetId)
      if (assetIndex !== -1) {
        this.assets[assetIndex].checked = true
      }
    },
    toggleViewableAssets() {
      if (this.canSeeMoreRows) {
        this.$refs.scrollableAssetsContainer.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      }
      nextTick(() => {
        this.canSeeMoreRows = !this.canSeeMoreRows
      })
    },
    transformAssets(assets) {
      const units = [
        { value: 1e12, symbol: 'T' },
        { value: 1e9, symbol: 'B' },
        { value: 1e6, symbol: 'M' },
        { value: 1e3, symbol: 'K' },
      ]

      return assets.map((asset) => {
        if (asset.checked === undefined) {
          asset.checked = false
        }

        let formattedEmissions
        if (asset.total_emissions < 1e3) {
          formattedEmissions = thousands(asset.total_emissions, 2, true)
        }
        else {
          for (const unit of units) {
            if (asset.total_emissions >= unit.value) {
              formattedEmissions = (asset.total_emissions / unit.value).toFixed(2) + unit.symbol
              break
            }
          }
        }
        asset.formattedEmissions = formattedEmissions
        asset.commaSeparatedFormatEmissions = thousands(asset.total_emissions, 2, true)

        return asset
      })
    },
    unCheckAsset(assetId) {
      const assetIndex = this.assets.findIndex(({ asset_id }) => asset_id === assetId)
      if (assetIndex !== -1) {
        this.assets[assetIndex].checked = false
      }
      const index = this.selectedAssets.indexOf(assetId)
      if (index !== -1) {
        this.selectedAssets.splice(index, 1)
      }
    },
  },
}
</script>
